import { errorMessage } from '@helpers/Constants';
import { Assets } from '@helpers/Constants/Assets';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@contexts/Toast';
import { Axios } from '@helpers/ApiService';
import { SubscriptionFooter } from '@components/SubscriptionFooter';
import { handleDateFormat } from '@services/OrderCreationService';
import { formatDateUSLong } from '@helpers/DateTime';
import {
  SubscriptionConfirmationProps,
  SubscriptionPlanProps,
} from '@helpers/Models';

export const SubscriptionConfirmation: React.FC<
  SubscriptionConfirmationProps
> = ({ isFromAppSettings }) => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();
  const planType = localStorage.getItem('planType');
  const subscriptionStatus = localStorage.getItem('subscriptionStatus');

  const [subscriptions, setSubscriptions] = useState<SubscriptionPlanProps>({
    id: 0,
    planName: '',
    description: '',
    price: 0,
    billingCycle: '',
    trialDays: 0,
    trialMaxAnimals: 0,
    isActive: 0,
  });
  const [billingDate, setBillingDate] = useState<string>('');

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  /**
   * fetchSubscriptions
   *
   * Retrieves subscription details for the current user from the server.
   *
   * Workflow:
   * - Sends a GET request to fetch subscription data.
   * - Extracts the first subscription from the response and updates state.
   * - Sets the billing date associated with the subscription.
   *
   * Error Handling:
   * - Displays an error toast message if the API request fails.
   *
   * Notes:
   * - Assumes that the subscriptions array is not empty.
   * - Consider adding a check to handle empty or undefined subscription data safely.
   *
   * Dependencies:
   * - Axios for API calls
   * - ApiEndpoints.FETCH_SUBSCRIPTION for endpoint reference
   * - setSubscriptions and setBillingDate for state updates
   * - showToastMessage for user notifications
   *
   * @async
   * @function fetchSubscriptions
   * @returns {Promise<void>}
   */
  const fetchSubscriptions = async (): Promise<void> => {
    try {
      const response = await Axios.get(ApiEndpoints.FETCH_SUBSCRIPTION);
      setSubscriptions(response.data.data.subscriptions[0]);
      setBillingDate(response.data.data.billingDate);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Handles post-subscription completion flow.
   *
   * This function is responsible for:
   * 1. Cleaning up temporary checkout-related data from localStorage.
   * 2. Setting subscription flags and status in localStorage.
   *    - `subscriptionActiveFlag`: Marks subscription as active.
   *    - `subscriptionStatus`:
   *        '1' → Trial plan
   *        '2' → Paid subscription plan
   * 3. Determining the user's next navigation step based on onboarding progress.
   *
   * Flow:
   * - Retrieves `onboardingSteps` from localStorage.
   * - Checks for the first incomplete onboarding step.
   *   - If an incomplete step exists → navigates user to that step.
   *   - If all steps are complete → navigates user to the dashboard.
   *
   * Dependencies:
   * - `localStorage` for persisting subscription and onboarding state.
   * - `navigate` (likely from react-router) for routing.
   * - `FrontendRoutes.DASHBOARD` for default landing page.
   *
   * Notes:
   * - Assumes `planType` is available in scope.
   * - Assumes `onboardingSteps` is an array of objects with:
   *      {
   *        isComplete: boolean;
   *        route: string;
   *      }
   * - Defaults to an empty array if `onboardingSteps` is not found in localStorage.
   *
   * @function handleSubscriptionCompletion
   * @returns {void}
   */
  const handleSubscriptionCompletion = () => {
    localStorage.removeItem('createCheckoutSession');
    localStorage.removeItem('planType');
    localStorage.removeItem('sessionId');
    localStorage.setItem('subscriptionActiveFlag', '1');
    localStorage.setItem('subscriptionStatus', planType == 'trial' ? '1' : '2'); // 1 - Trial; 2 - Subscription Plan

    // Get onboarding steps from localStorage (fallback to empty array)
    const onboardingSteps = JSON.parse(
      localStorage.getItem('onboardingSteps') || '[]',
    );

    // Find the first incomplete onboarding step
    const incompleteStep = onboardingSteps.find(
      (step: { isComplete: boolean }) => !step.isComplete,
    );

    // If onboarding is incomplete, navigate to that step
    if (incompleteStep) {
      navigate(incompleteStep.route);
      return;
    } else {
      // Otherwise, navigate to dashboard
      navigate(FrontendRoutes.DASHBOARD);
      return;
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative px-4">
      {isFromAppSettings != true && (
        <button
          onClick={handleSubscriptionCompletion}
          type="button"
          className="absolute top-4 right-4 text-xl text-gray-500 hover:text-gray-800"
        >
          ✕
        </button>
      )}
      <div className="flex-1" />

      {/* Main Content */}
      <div className="w-full max-w-md flex flex-col items-center gap-6 text-center flex-1">
        <div className="flex flex-col items-center gap-3 xl:mt-6 lg:mt-6">
          <span className="text-[#38B83F] text-4xl">
            Subscription Activated!
          </span>
          <img
            src={Assets.CONFIRMATION_SUCCESS}
            alt="Success"
            className="w-24 h-24 object-contain"
          />
          {isFromAppSettings != true && (
            <h2 className="text-xl text-[#7F7F7F] font-semibold font-manrope">
              Your {planType == 'trial' ? 'trial period' : 'subscription'} has
              begun
            </h2>
          )}
        </div>

        {/* Plan card */}
        <div
          className="w-full px-8 py-4"
          style={{
            borderWidth: '4px 2px 2px 2px',
            borderStyle: 'solid',
            borderImage:
              'linear-gradient(224.86deg, #73CCD8 4.87%, #2B6B9F 96.04%) 1',
          }}
        >
          <div className="text-[#292D32] flex flex-col items-center">
            <span className="font-extrabold text-xl">
              {subscriptions.planName}
            </span>
            <span className="text-sm text-[#6C757D]">
              {subscriptions.description}
            </span>
          </div>

          <div className="text-[#292D32] flex flex-col items-center mt-2">
            <div>
              <span className="font-bold text-2xl">$</span>
              <span className="text-4xl"> {subscriptions.price}</span>
              <span className="text-[#6C757D]"> / animal</span>
            </div>
            <span className="mt-2 text-[#6C757D]">
              {subscriptions.billingCycle}
            </span>
          </div>

          {/* Trial badge */}
          {isFromAppSettings != true && (
            <>
              {planType == 'trial' && (
                <div className="mt-6 p-2 flex items-center justify-center bg-[#F8F8F8] text-[#170B00]">
                  <span>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 19.0625V8.0625H0V2.0625H5.2C5.11667 1.9125 5.0625 1.75417 5.0375 1.5875C5.0125 1.42083 5 1.24583 5 1.0625C5 0.229167 5.29167 -0.479167 5.875 -1.0625C6.45833 -1.64583 7.16667 -1.9375 8 -1.9375C8.38333 -1.9375 8.74167 -1.86667 9.075 -1.725C9.40833 -1.58333 9.71667 -1.3875 10 -1.1375C10.2833 -1.40417 10.5917 -1.60417 10.925 -1.7375C11.2583 -1.87083 11.6167 -1.9375 12 -1.9375C12.8333 -1.9375 13.5417 -1.64583 14.125 -1.0625C14.7083 -0.479167 15 0.229167 15 1.0625C15 1.24583 14.9833 1.41667 14.95 1.575C14.9167 1.73333 14.8667 1.89583 14.8 2.0625H20V8.0625H18V19.0625H2ZM11.2875 0.35C11.0958 0.541667 11 0.779167 11 1.0625C11 1.34583 11.0958 1.58333 11.2875 1.775C11.4792 1.96667 11.7167 2.0625 12 2.0625C12.2833 2.0625 12.5208 1.96667 12.7125 1.775C12.9042 1.58333 13 1.34583 13 1.0625C13 0.779167 12.9042 0.541667 12.7125 0.35C12.5208 0.158333 12.2833 0.0625 12 0.0625C11.7167 0.0625 11.4792 0.158333 11.2875 0.35ZM7 1.0625C7 1.34583 7.09583 1.58333 7.2875 1.775C7.47917 1.96667 7.71667 2.0625 8 2.0625C8.28333 2.0625 8.52083 1.96667 8.7125 1.775C8.90417 1.58333 9 1.34583 9 1.0625C9 0.779167 8.90417 0.541667 8.7125 0.35C8.52083 0.158333 8.28333 0.0625 8 0.0625C7.71667 0.0625 7.47917 0.158333 7.2875 0.35C7.09583 0.541667 7 0.779167 7 1.0625ZM2 4.0625V6.0625H9V4.0625H2ZM9 17.0625V8.0625H4V17.0625H9ZM11 17.0625H16V8.0625H11V17.0625ZM18 6.0625V4.0625H11V6.0625H18Z"
                        fill="#434343"
                      />
                    </svg>
                  </span>
                  <span className="ml-2">
                    {subscriptions.trialDays}-day trial or{' '}
                    {subscriptions.trialMaxAnimals} animals
                  </span>
                </div>
              )}
              <div className="flex flex-row gap-4 pr-2 mt-6">
                <span className="text-[#292D32]">
                  {planType == 'trial' ? 'Trial Start Date' : 'Plan Start Date'}
                  :
                </span>
                {/* Current date here */}
                <span className="text-[#292D32]">
                  <span className="font-medium">
                    {formatDateUSLong(new Date())}
                  </span>
                </span>
              </div>
            </>
          )}
          <div className="flex flex-row gap-4 pr-2 mt-3">
            <span className="text-[#292D32]">Renewal:</span>
            <span className="text-[#292D32] font-medium">
              {planType == 'trial'
                ? 'Renews automatically after trial period.'
                : 'Renews automatically monthly.'}
            </span>
          </div>
          <div className="flex flex-row gap-4 pr-2 mt-3">
            <span className="text-[#292D32]">Next Billing Date:</span>
            <span className="text-[#292D32] font-medium">
              {handleDateFormat(billingDate)}
            </span>
          </div>
        </div>
        {/* 3 - Trial; 4 - Pending; 5 - Cancelled */}
        {isFromAppSettings != true &&
          !['3', '4', '5'].includes(subscriptionStatus ?? '') && (
            <button
              onClick={handleSubscriptionCompletion}
              type="submit"
              className="w-full px-6 py-2 mb-5 rounded-md font-medium text-white bg-primary hover:bg-dark-primary"
            >
              Continue and Set Up Store Configuration
            </button>
          )}
      </div>

      <div className="flex-1 mt-3" />
      {/* Footer */}
      <SubscriptionFooter isFromAppSettings={true} />
      <span className="mt-3"></span>
    </div>
  );
};
